import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getScripts, deleteScript, Script, getTasksForProject, Task, exportScripts } from '../../services/api';
import ScriptModal from './ScriptModal';
import BatchGenerateModal from './BatchGenerateModal';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassBadge } from '../ui/GlassBadge';
import { Plus, Zap, Download, CheckCircle, Clock } from 'lucide-react';
import './Scripts.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const FORMAT_LABELS: Record<string, { label: string; duration: string }> = {
    short_form: { label: 'Short-form', duration: '60-90 sec' },
    explainer: { label: 'Explainer', duration: '2-3 min' },
    ad_spot: { label: 'Ad Spot', duration: '15-30 sec' },
    long_form: { label: 'Long-form', duration: '5-10 min' },
};

const cleanScriptText = (raw: string): string => {
    let text = raw.replace(/Generate article:\s*/gi, '');
    const bodyStart = text.search(/^\*\*/m);
    if (bodyStart > 0) text = text.slice(bodyStart);
    return text.trim();
};

const stripHtml = (text: string): string => {
    if (!text) return '';
    if (text.trim().startsWith('<')) {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        return (doc.body.textContent || '').replace(/Generate article:\s*/gi, '').trim();
    }
    return cleanScriptText(text)
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/^\s*[-*+]\s+/gm, '');
};

const normalizeStream = (s: string): string =>
    s.replace(/^\d+[\.\)]\s*/, '').replace(/\s*\(.*?\)\s*/g, '').replace(/\s+/g, ' ').trim();

interface OutletContext {
    selectedProjectId?: string;
}

const ScriptsList: React.FC = () => {
    const navigate = useNavigate();
    const { selectedProjectId } = useOutletContext<OutletContext>();
    const [scripts, setScripts] = useState<Script[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [modalScript, setModalScript] = useState<Script | null>(null);
    const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [allTasks, setAllTasks] = useState<Task[]>([]);
    const [streamFilter, setStreamFilter] = useState('');
    const [calendarFilter, setCalendarFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [sortBy, setSortBy] = useState<'created_at' | 'calendar_date'>('created_at');

    useEffect(() => {
        loadScripts();
        setStreamFilter('');
        setCalendarFilter('');
        setStatusFilter('');
        setAllTasks([]);
        if (selectedProjectId) {
            getTasksForProject(selectedProjectId)
                .then(setAllTasks)
                .catch(() => setAllTasks([]));
        }
    }, [selectedProjectId]);

    const taskStreamMap = useMemo(() => {
        const map: Record<string, string> = {};
        for (const t of allTasks) {
            if (t.input_data?.masterplan_stream) {
                map[t.task_id] = t.input_data.masterplan_stream;
            }
        }
        return map;
    }, [allTasks]);

    const calendars = useMemo(() => {
        return allTasks
            .filter(t => t.input_data?.quick_win_type === 'content_calendar')
            .map(t => ({
                task_id: t.task_id,
                name: t.name,
                articleTaskIds: new Set<string>(
                    ((t.output_data?.calendar || []) as any[])
                        .map((row: any) => row.task_id)
                        .filter(Boolean)
                ),
            }));
    }, [allTasks]);

    const taskDateMap = useMemo(() => {
        const map: Record<string, string> = {};
        for (const t of allTasks) {
            if (t.input_data?.quick_win_type !== 'content_calendar') continue;
            for (const row of (t.output_data?.calendar || []) as any[]) {
                if (row.task_id && row.date && !map[row.task_id]) {
                    map[row.task_id] = row.date;
                }
            }
        }
        return map;
    }, [allTasks]);

    const taskNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        for (const t of allTasks) {
            map[t.task_id] = t.name;
        }
        return map;
    }, [allTasks]);

    const availableStreams = useMemo(() => {
        const seen = new Set<string>();
        for (const s of scripts) {
            if (s.task_id && taskStreamMap[s.task_id]) {
                seen.add(taskStreamMap[s.task_id]);
            }
        }
        return Array.from(seen).sort();
    }, [scripts, taskStreamMap]);

    const filteredScripts = useMemo(() => {
        return scripts.filter(s => {
            if (streamFilter && taskStreamMap[s.task_id || ''] !== streamFilter) return false;
            if (calendarFilter) {
                const cal = calendars.find(c => c.task_id === calendarFilter);
                if (!cal || !s.task_id || !cal.articleTaskIds.has(s.task_id)) return false;
            }
            if (statusFilter && s.approval_status !== statusFilter) return false;
            return true;
        });
    }, [scripts, streamFilter, calendarFilter, statusFilter, taskStreamMap, calendars]);

    const sortedScripts = useMemo(() => {
        if (sortBy !== 'calendar_date') return filteredScripts;
        return [...filteredScripts].sort((a, b) => {
            const da = taskDateMap[a.task_id || ''];
            const db = taskDateMap[b.task_id || ''];
            if (!da && !db) return 0;
            if (!da) return 1;
            if (!db) return -1;
            return da.localeCompare(db);
        });
    }, [filteredScripts, sortBy, taskDateMap]);

    const loadScripts = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getScripts(selectedProjectId);
            setScripts(data);
        } catch (err: any) {
            setError('Failed to load scripts');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (scriptId: string) => {
        if (!window.confirm('Delete this script? This cannot be undone.')) return;
        try {
            await deleteScript(scriptId);
            setScripts(prev => prev.filter(s => s.script_id !== scriptId));
        } catch (err: any) {
            alert(getApiError(err, 'Failed to delete script'));
        }
    };

    const handleScriptSaved = (updated: Script) => {
        setScripts(prev => prev.map(s => s.script_id === updated.script_id ? updated : s));
        setModalScript(updated);
    };

    const formatDate = (iso: string) => {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const buildExportFilename = (): string => {
        const slugify = (s: string) =>
            s.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
        const parts: string[] = ['scripts'];
        if (streamFilter) parts.push(slugify(normalizeStream(streamFilter)));
        if (calendarFilter) {
            const cal = calendars.find(c => c.task_id === calendarFilter);
            if (cal) parts.push(slugify(cal.name));
        }
        if (statusFilter) parts.push(statusFilter);
        parts.push(new Date().toISOString().slice(0, 10));
        return parts.join('-') + '.zip';
    };

    const handleExport = async () => {
        if (filteredScripts.length === 0) return;
        setIsExporting(true);
        try {
            const blob = await exportScripts(filteredScripts.map(s => s.script_id));
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = buildExportFilename();
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            setError('Failed to export scripts. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
        <div className="scripts-container">
            <div className="scripts-header">
                <h1>Scripts</h1>
                <div className="scripts-header-actions">
                    {selectedProjectId && (
                        <GlassButton 
                            variant="secondary" 
                            onClick={() => setShowBatchModal(true)}
                            icon={<Zap size={16} />}
                        >
                            Batch Generate
                        </GlassButton>
                    )}
                    <GlassButton 
                        variant="primary" 
                        onClick={() => navigate('/scripts/create')}
                        icon={<Plus size={18} />}
                    >
                        Create Script
                    </GlassButton>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="scripts-filter-bar">
                {selectedProjectId && availableStreams.length > 0 && (
                    <select
                        value={streamFilter}
                        onChange={e => setStreamFilter(e.target.value)}
                        aria-label="Filter by stream"
                    >
                        <option value="">All Streams</option>
                        {availableStreams.map(s => (
                            <option key={s} value={s}>{normalizeStream(s)}</option>
                        ))}
                    </select>
                )}
                {selectedProjectId && calendars.length > 0 && (
                    <select
                        value={calendarFilter}
                        onChange={e => setCalendarFilter(e.target.value)}
                        aria-label="Filter by calendar"
                    >
                        <option value="">All Calendars</option>
                        {calendars.map(c => (
                            <option key={c.task_id} value={c.task_id}>{c.name}</option>
                        ))}
                    </select>
                )}
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    aria-label="Filter by status"
                >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                </select>
                <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={handleExport}
                    disabled={isExporting || filteredScripts.length === 0}
                    icon={<Download size={16} />}
                >
                    {isExporting ? 'Exporting…' : `Export (${filteredScripts.length})`}
                </GlassButton>
                <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value as 'created_at' | 'calendar_date')}
                    aria-label="Sort order"
                    style={{ marginLeft: 'auto' }}
                >
                    <option value="created_at">Sort: Added Date</option>
                    <option value="calendar_date">Sort: Calendar Date</option>
                </select>
            </div>

            {loading ? (
                <div className="empty-state"><p>Loading scripts...</p></div>
            ) : scripts.length === 0 ? (
                <div className="empty-state">
                    <p>No scripts yet. Generate your first video script from an article.</p>
                    <GlassButton 
                        variant="primary" 
                        onClick={() => navigate('/scripts/create')}
                        icon={<Plus size={18} />}
                    >
                        Create Script
                    </GlassButton>
                </div>
            ) : filteredScripts.length === 0 ? (
                <div className="empty-state"><p>No scripts match your filters.</p></div>
            ) : (
                <div className="scripts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                    {sortedScripts.map(script => {
                        const fmt = FORMAT_LABELS[script.video_format] || { label: script.video_format, duration: '' };
                        const preview = stripHtml(script.script_content || '').slice(0, 200);
                        return (
                            <GlassCard key={script.script_id} variant="default" padding="md" className="script-card">
                                <div className="script-card-header">
                                    <h3 className="script-card-title" style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 500 }}>
                                        {(script.task_id && taskNameMap[script.task_id]) || script.title}
                                    </h3>
                                    <div className="script-card-badges" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <GlassBadge
                                            variant={script.video_format === 'short_form' ? 'info' : script.video_format === 'explainer' ? 'success' : 'default'}
                                            size="sm"
                                        >
                                            {fmt.label}
                                        </GlassBadge>
                                        <GlassBadge
                                            variant={script.approval_status === 'approved' ? 'success' : 'warning'}
                                            size="sm"
                                        >
                                            {script.approval_status === 'approved' ? 'Approved' : 'Pending'}
                                        </GlassBadge>
                                    </div>
                                </div>
                                <div className="script-card-date" style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                    {fmt.duration && <span style={{ marginRight: '8px' }}>{fmt.duration}</span>}
                                    {formatDate(script.created_at)}
                                </div>
                                {preview && (
                                    <div className="script-card-preview" style={{
                                        marginTop: '12px',
                                        padding: '12px',
                                        background: 'var(--panel-strong)',
                                        borderRadius: 'var(--radius-md)',
                                        fontSize: '13px',
                                        color: 'var(--text-secondary)',
                                        lineHeight: 1.5
                                    }}>
                                        {preview}{preview.length === 200 ? '…' : ''}
                                    </div>
                                )}
                                <div className="script-card-footer" style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <div className="script-card-actions" style={{ display: 'flex', gap: '8px' }}>
                                        <GlassButton variant="ghost" size="sm" onClick={() => { setModalScript(script); setModalMode('view'); }}>
                                            View
                                        </GlassButton>
                                        <GlassButton variant="ghost" size="sm" onClick={() => { setModalScript(script); setModalMode('edit'); }}>
                                            Edit
                                        </GlassButton>
                                    </div>
                                    <GlassButton variant="danger" size="sm" onClick={() => handleDelete(script.script_id)}>
                                        Delete
                                    </GlassButton>
                                </div>
                            </GlassCard>
                        );
                    })}
                </div>
            )}
        </div>

        {modalScript && (
            <ScriptModal
                script={modalScript}
                initialMode={modalMode}
                onClose={() => setModalScript(null)}
                onSaved={handleScriptSaved}
                articleTitle={(modalScript.task_id && taskNameMap[modalScript.task_id]) || undefined}
            />
        )}
        {showBatchModal && selectedProjectId && (
            <BatchGenerateModal
                projectId={selectedProjectId}
                onClose={() => setShowBatchModal(false)}
                onComplete={newScripts => setScripts(prev => [...newScripts, ...prev])}
            />
        )}
        </>
    );
};

export default ScriptsList;
